import { defineStore } from "pinia";
import { CommonHttp } from "@/models/interfaces/http/common.http.request";
import { reactive, shallowReactive } from "vue";
import { rootStore } from "./index";
import { generalStore } from "./general";
import { AnalyticsResultOrderedAsker, AnalyticsResultTableExtended, CellDataSimple } from "@/models/interfaces/entity-settings/querytable/querytable";
import { BaseListItem, getIdentityKind, QueryResultItem } from "@/components/common/base-list/interfaces/ListTypes";
import { AnalyticsChartResultBase, AnalyticsElementIdentity } from "@/models/interfaces/entity-settings/charts/charts";
import { BaseItemTypes } from "@/components/dnd/ItemTypes";
import { AnalyticsV2ChartType, ColumnCategory } from "@/models/etc/types";
import { PaginationProps } from "@/models/base/common.selector";
import { auxStore } from "./auxillary";
import _ from "lodash";

/**
 * main table and charts methods store; contains the resulting data displayed in the right window part
 */

const API_URL = process.env.VUE_APP_API_URL

export type QueryState = {
    loading: boolean;
    tableReloaded: boolean;
    // ignore the request result and don't mutate the model after the request is finished
    stop_request: boolean;
    // actual data
    resultingTable: AnalyticsResultTableExtended;
    resultingGraph: AnalyticsChartResultBase;

    resultingReport: QueryResultItem[];

    // Map<entityKey, queryStruct>
    tableWidths: Map<number, number>;
    reqQueue: Map<string, AbortController>;
};

export const queryStore = defineStore({
  id: "queryStore",
  state: () =>
    ({
        loading: false,
        tableReloaded: false,
        // ignore the request result and don't mutate the model after the request is finished
        stop_request: false,
        // reactive filters change and update directly from interface
        resultingTable: reactive({} as AnalyticsResultTableExtended),
        // reactive filters change and update directly from interface
        resultingGraph: reactive({} as AnalyticsChartResultBase),
        resultingReport: reactive([]),
        tableWidths: new Map(),
        reqQueue: new Map()
    } as QueryState),

  actions: {
    // stopRequest() {
    //   this.stop_request = true
    //   this.clearSettings()
    // },

    setColumnWidth(col_i: number, col_w: number) {
      this.tableWidths.set(col_i, col_w);
    },

    resetWidths() {
      this.tableWidths.clear();
    },

    pushToReport(arg: BaseListItem) {
      this.resultingReport.push({
        elementItem: arg,
        elementType: BaseItemTypes.BREAK,
        elementSegmentType: arg.DestType,
        Element: undefined,
        selectedAll: '',
        selectedEntities: reactive([]),
        unselectedEntities: reactive([]),
        pagination: {} as PaginationProps
      })
    },

    // table
    // report_index is ignored if addToReport is false
    async getResultTable(arg: BaseListItem, pag: any, addToReport: boolean = false, report_index: number = 0) {
      const rootstore = rootStore()
      const auxstore = auxStore()
      this.stop_request = false

      const http = new CommonHttp()
      try {
        this.loading = true
        let response = undefined

        const entkey = arg.elType+'-'+(arg.SrcType === undefined ? 'null' : arg.SrcType)+ 
          '-'+arg.DestType+'-'+arg.Id;

        // abort all other entity/graph result connections because only one element can be displayed at a time
        // except report elements
        if (!addToReport) {
          Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
            if (key != entkey) {
              console.log('abort conn '+key)
              this.reqQueue.get(key)?.abort();
              this.reqQueue.delete(key);
            }
          });
        }

        // console.log(pag)
        // FormulaResults бэк считает и присылает только на /Initial (и при пересчёте).
        // На /Ordered и /AnotherPage формулы не меняются, поэтому запоминаем, был ли
        // это Initial-запрос: если нет, при применении ответа не даём затереть уже
        // посчитанные результаты формул пустыми значениями (см. ниже).
        let requestedInitial = false;
        if (/*!pag.offset*/ pag.recalculate || pag.forceInitial) {
          // forceOrdered принудительно отправляет на /Ordered (например, сброс
          // сортировки по закреплённым: нужно явно слать OrderByPinned=false, а не
          // уходить на /Initial). Флаг одноразовый — гасим сразу после использования.
          const forceOrdered = !!(pag as any).forceOrdered;
          (pag as any).forceOrdered = false;
          if ((!pag.sortMode && !pag.OrderByPinned && !forceOrdered) || pag.recalculate) {
            requestedInitial = true;

            const controller = new AbortController();
            this.reqQueue.set(entkey, controller);

            // reset if table changes
            this.resetWidths();

            await http.post(`Analytics/General/GetTableResult/Initial`, {
              Identity: {
                ElementId: arg.Id,
                Source: arg.SrcType !== undefined && arg.SrcType !== null ? arg.SrcType : null,
                Destination: arg.DestType,
                Kind: getIdentityKind(arg)
              } as AnalyticsElementIdentity,
              Count: pag.count ? pag.count : Number(auxstore.getTableRowCount),
              ForceRecalculate: pag.recalculate
            }, { signal: controller.signal })
            .then(res => {
              response = res
              this.reqQueue.delete(entkey);
            }).catch(err => {
              console.log('error 2')
              this.reqQueue.delete(entkey);
            });
          } else {
            const controller = new AbortController();
            this.reqQueue.set(entkey, controller);

            await http.post(`Analytics/General/GetTableResult/Ordered`, {
              Identity: {
                ElementId: arg.Id,
                Source: arg.SrcType !== undefined && arg.SrcType !== null ? arg.SrcType : null,
                Destination: arg.DestType,
                Kind: getIdentityKind(arg)
              } as AnalyticsElementIdentity,
              Count: pag.count ? pag.count : Number(auxstore.getTableRowCount),
              OrderMode: pag.sortMode,
              ColumnCategory: pag.sortCategory,
              OrderingEntityIds: Array.isArray(pag.OrderingEntityIds)
                ? pag.OrderingEntityIds
                : [],
              OrderByPinned: pag.OrderByPinned
            } as AnalyticsResultOrderedAsker, { signal: controller.signal }).then(res => {
              response = res
              this.reqQueue.delete(entkey);
            }).catch(err => {
              console.log('error 2')
              this.reqQueue.delete(entkey);
            });
          }
        } else {
          const controller = new AbortController();
          this.reqQueue.set(entkey, controller);

          await http.post(`Analytics/General/GetTableResult/AnotherPage`, {
            Identity: {
              ElementId: arg.Id,
              Source: arg.SrcType !== undefined && arg.SrcType !== null ? arg.SrcType : null,
              Destination: arg.DestType,
              Kind: getIdentityKind(arg)
            } as AnalyticsElementIdentity,
            Count: pag.count ? pag.count : Number(auxstore.getTableRowCount),
            Offset: pag.offset
          }, { signal: controller.signal }).then(res => {
            response = res
            this.reqQueue.delete(entkey);
          }).catch(err => {
            console.log('error 2')
            this.reqQueue.delete(entkey);
          });
        }

        // change ParentRecordId: 161648 to ParentRecordId: 373268
        

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // Устаревшие ответы по другим элементам уже отсекаются через AbortController
          // выше: при смене элемента его прошлый запрос прерывается и сюда не доходит.
          // Поэтому данные текущего элемента применяем всегда. Раньше тут была доп.
          // проверка !genstore.isLoading, и при гонке с загрузкой общего списка валидный
          // ответ текущего элемента терялся — таблица оставалась пустой через раз.
          if (!this.stop_request) {
            const { data } = response;

            if (addToReport) {
              if (!pag.offset && pag.initial) {
                this.resultingReport.push({
                  elementItem: arg,
                  elementType: (arg as any).elType !== undefined ? (arg as any).elType : (arg.SrcType ? BaseItemTypes.PROJECTION : BaseItemTypes.SELECTION),
                  elementSegmentType: arg.DestType,
                  Element: reactive(data),
                  selectedAll: '',
                  selectedEntities: reactive([]),
                  unselectedEntities: reactive([]),
                  pagination: {
                    offset: pag.offset,
                    count: Number(auxstore.getTableRowCount),
                    sortCategory: pag.sortCategory,
                    sortMode: pag.sortMode,
                    recalculate: false,
                    forceInitial: true,
                    report: true,
                    OrderByPinned: (data as AnalyticsResultTableExtended).PinnedOptions.OrderByPinned,
                    total_count: (data as AnalyticsResultTableExtended).Count,
                    report_index: this.resultingReport.length
                  }
                })
              } else {
                this.resultingReport[report_index].Element = reactive(data as AnalyticsResultTableExtended);
                this.resultingReport[report_index].pagination = pag
              }
            } else {
              this.tableReloaded = true;
              // Формулы приходят только с /Initial. Для /Ordered и /AnotherPage
              // переносим уже посчитанные FormulaResults из текущей модели, чтобы
              // ответ без формул (или с пустыми) их не затирал.
              if (!requestedInitial) {
                const prevFormulas = (this.resultingTable as AnalyticsResultTableExtended)?.FormulaResults;
                if (prevFormulas) (data as AnalyticsResultTableExtended).FormulaResults = prevFormulas;
              }
              // reactive filters change and update directly from interface
              this.resultingTable = reactive({} as AnalyticsResultTableExtended);
              this.resultingTable = reactive(data);
            }
          } else {
            console.log('error 1')
            console.log(response)
          }

          const that = this
          setTimeout(() => {
            that.loading = false
          }, 500);

          return { response_ok: true }
        }
      } catch (e) {
        console.log('error 8')
        console.log(JSON.stringify(e))

        const that = this
        setTimeout(() => {
          that.loading = false
        }, 500);
      }

      const that = this
      setTimeout(() => {
        that.loading = false
      }, 500);
      return { response_ok: false }
    },

    // chart result
    async getResultGraph(arg: any, addToReport: boolean = false, argbase: undefined) {
      const rootstore = rootStore()
      this.stop_request = false

      const entkey = arg.elType+'-'+(arg.SrcType === undefined ? 'null' : arg.SrcType)+ 
        '-'+arg.DestType+'-'+arg.Id;

      // abort all other entity/graph result connections because only one element can be displayed at a time
      // except report elements
      if (!addToReport) {
        Array.from(this.reqQueue.entries()).forEach(([key, value]) => {
          if (key != entkey) {
            console.log('abort conn '+key)
            this.reqQueue.get(key)?.abort();
            this.reqQueue.delete(key);
          }
        });
      }

      const controller = new AbortController();
      this.reqQueue.set(entkey, controller);

      const http = new CommonHttp()
      try {
        this.loading = true
        let response = undefined
        await http.post(`Analytics/Charts/GetResult/`, arg, { signal: controller.signal }).then(res => {
          response = res
          this.reqQueue.delete(entkey);
        }).catch(err => {
          console.log('error 24')
          this.reqQueue.delete(entkey);
        });

        // @ts-ignore
        if (response && (response.status === 200 || response.status === 201)) {
          // don't update data if genstore is loading again because this data is obsolette
          const genstore = generalStore()
          if (!genstore.isLoading && !this.stop_request) {
            const { data } = response;
            
            if (addToReport) {
              this.resultingReport.push({
                elementGraphItem: arg,
                elementItem: argbase,
                elementType: arg.ChartType === AnalyticsV2ChartType.Bar ? BaseItemTypes.BAR_CHART : 
                  (AnalyticsV2ChartType.Linear ? BaseItemTypes.GRAPH : BaseItemTypes.PIE_CHART),
                elementSegmentType: arg.Destination,
                Element: reactive(data)
              })
            } else {
              this.resultingGraph = reactive({} as AnalyticsChartResultBase);
              // reactive filters change and update directly from interface
              this.resultingGraph = reactive(data);
            }
          } else {
            console.log('error 1')
            console.log(response)
          }

          this.loading = false
          return { response_ok: true }
        }
      } catch (e) {
        console.log('error 8')
        console.log(JSON.stringify(e))
        this.loading = false
      }

      this.loading = false
      return { response_ok: false }
    },

    abortAllPending() {
      this.reqQueue.forEach((controller) => controller.abort())
      this.reqQueue.clear()
    },

    clearSettings() {
      // reactive filters change and update directly from interface
      this.clearTable();
      this.clearGraph();
      this.resultingReport = reactive([] as QueryResultItem[]);
    },

    clearGraph() {
      // reactive filters change and update directly from interface
      this.resultingGraph = reactive({} as AnalyticsChartResultBase);
      this.resultingReport = reactive([] as QueryResultItem[]);
    },

    clearTable() {
      // reactive filters change and update directly from interface
      this.resultingTable = reactive({} as AnalyticsResultTableExtended);
      this.resultingGraph = reactive({} as AnalyticsChartResultBase);
      this.resultingReport = reactive([] as QueryResultItem[]);
    },

    stripTableRows(entityId: number, newId: number, newVCode: string, clone: boolean = false) {
      this.tableReloaded = false;
      const newt = _.cloneDeep(this.resultingTable)
      const tindex = newt.DataRows.findIndex(el => el.EntityId === entityId);

      if (clone) {
        const newRow = _.cloneDeep(newt.DataRows[tindex])
        newRow.EntityId = newId;
        newRow.CellContainers.forEach(cellCont => {
          cellCont.Cells = cellCont.Cells.map(cell => {
            if (cell.Category === ColumnCategory.ItemVendorCode) {
              return {
                ...cell,
                Data: cell.Data.map(data_el => {
                  return {
                    ...data_el,
                    Value: newVCode
                  } as CellDataSimple
                })
              }
            } else {
              return cell
            }
          })
        })
        console.log(newRow)
        newt.DataRows.splice(tindex + 1, 1, newRow);  
      } else {
        newt.DataRows.splice(tindex, 1);
      }
      
      this.resultingTable = reactive(newt)
    }
  },

  getters: {
    
    isLoading(state): boolean {
      return state.loading
    },

    tableWasReloaded(state): boolean {
      return state.tableReloaded
    },

    getCurrentTableWidths(state): Map<number, number> {
      return state.tableWidths
    },

    getCurrentTable(state): AnalyticsResultTableExtended {
      return state.resultingTable
    },

    getCurrentGraph(state): AnalyticsChartResultBase {
      return state.resultingGraph
    },

    getCurrentReport(state): QueryResultItem[] {
      return state.resultingReport
    },
  }
});
